import * as React from 'react';
import {useEffect, useState} from "react";
import {Box, Button, CircularProgress, IconButton, Paper, Portal, Tooltip, Typography} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import CommentsDisabledOutlinedIcon from '@mui/icons-material/CommentsDisabledOutlined';
import {DragDropContext, Draggable, Droppable} from "react-beautiful-dnd";
import {Outlet} from "react-router";
import AdminProductCard from "./AdminProductCard";
import BulkReadyCommentDialog from "./BulkReadyCommentDialog";
import ResponseDialog from "../ResponseDialog";
import {
    useBulkUpdateReadyCommentMutation,
    useGetAllProductsAdminQuery,
    useUpdateAllProductsMutation
} from "../../core/api/products-api";


export default function AdminShop(props) {
    const {data: products = [], isFetching, isSuccess, refetch} = useGetAllProductsAdminQuery(props.locationFilter);

    const [updateAll] = useUpdateAllProductsMutation();
    const [removeReadyComment, removeReadyCommentResponse] = useBulkUpdateReadyCommentMutation();

    const [itemList, setItemList] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkState, setBulkState] = useState({
        isBulkDialogOpen: false,
        isRemoveSubmitted: false,
        isResponseDialogOpen: false,
        responseData: {}
    });

    useEffect(() => {
        setItemList(products.map(x => {
            return {
                id : x.id,
                content : x
            }
        }).sort(function (a,b){
            let indexA = a.content.sortingOrder;
            let indexB = b.content.sortingOrder;
            if (indexA < indexB) {
                return -1;
            }
            return 1;
        }))
    }, [products]);

    useEffect(() => {
        if (bulkState.isRemoveSubmitted &&
            (removeReadyCommentResponse.isSuccess || removeReadyCommentResponse.isError)) {
            setBulkState({
                ...bulkState,
                isRemoveSubmitted: false,
                isResponseDialogOpen: true,
                responseData: removeReadyCommentResponse
            })
            if (removeReadyCommentResponse.isSuccess) {
                setSelectedIds([]);
            }
        }
    }, [removeReadyCommentResponse]);


    const handleDrop = (droppedItem) => {
        if (!droppedItem.destination) return;
        let updatedList = itemList.map(item => ({...item, content: {...item.content}}));
        const [reorderedItem] = updatedList.splice(droppedItem.source.index, 1);
        updatedList.splice(droppedItem.destination.index, 0, reorderedItem);
        updatedList.forEach((x, index) => {
            x.content.sortingOrder = index
        })
        setItemList(updatedList);
        updateAll({
            productList : updatedList.map(i => ({id : i.content.id, sortingOrder : i.content.sortingOrder}))
        });
    };

    const handleToggleSelect = (productId) => {
        setSelectedIds(selectedIds.includes(productId) ?
            selectedIds.filter(id => id !== productId) :
            [...selectedIds, productId]);
    }

    const handleBulkDialogClose = (isResponseReady, responseData) => {
        setBulkState({
            ...bulkState,
            isBulkDialogOpen: false,
            isResponseDialogOpen: isResponseReady === true,
            responseData: isResponseReady === true ? responseData : {}
        })
        if (isResponseReady === true && responseData.isSuccess) {
            setSelectedIds([]);
        }
    }

    const handleRemoveReadyComment = () => {
        setBulkState({
            ...bulkState,
            isRemoveSubmitted: true
        })
        removeReadyComment({
            productIds: selectedIds,
            hasReadyComment: false
        });
    }

    const handleResponseDialogClose = () => {
        setBulkState({
            ...bulkState,
            isResponseDialogOpen: false
        })
    }

    const isSelectionActive = selectedIds.length > 0;

    return (
        <Box sx={{pb: isSelectionActive ? 12 : 0}}>
            {isFetching ?
                <Box sx={{display: 'flex'}}>
                    <CircularProgress/>
                </Box> :
                <Box sx={{display: 'flex', flexWrap: 'wrap'}} className={"product-cards"}>
                    {
                        isSuccess && itemList.length !== 0 ?
                            <DragDropContext onDragEnd={handleDrop}>
                                <Droppable droppableId="list-container">
                                    {(provided) => (
                                        <div className={"droppable-field"}
                                             {...provided.droppableProps}
                                             ref={provided.innerRef}
                                        >
                                            {itemList.map((item, index) => (
                                                <Draggable key={item.id}
                                                           draggableId={item.id}
                                                           index={index}
                                                           isDragDisabled={isSelectionActive}>
                                                    {(provided) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                        >
                                                            <AdminProductCard
                                                                product={item.content}
                                                                refetchProduct={refetch}
                                                                dragHandleProps={provided.dragHandleProps}
                                                                isSelected={selectedIds.includes(item.id)}
                                                                onToggleSelect={handleToggleSelect}
                                                            />
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext> :
                            <Box>
                                There are no products in the shop yet
                            </Box>
                    }
                    <Outlet/>
                </Box>
            }
            {isSelectionActive &&
                <Portal>
                    <Paper elevation={6}
                           className="bulk-actions-bar"
                           sx={{
                               position: 'fixed',
                               bottom: 16,
                               left: '50%',
                               transform: 'translateX(-50%)',
                               width: 'max-content',
                               maxWidth: 'calc(100vw - 32px)',
                               p: 1.5,
                               display: 'flex',
                               alignItems: 'center',
                               gap: 2,
                               borderRadius: 3,
                               zIndex: (theme) => theme.zIndex.snackbar
                           }}>
                        <Typography sx={{fontWeight: 600, ml: 1}}>
                            {selectedIds.length} selected
                        </Typography>
                        <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            startIcon={<CommentOutlinedIcon/>}
                            onClick={() => setBulkState({...bulkState, isBulkDialogOpen: true})}
                        >
                            Set auto-comment
                        </Button>
                        <Button
                            variant="outlined"
                            color="inherit"
                            size="small"
                            startIcon={<CommentsDisabledOutlinedIcon/>}
                            onClick={handleRemoveReadyComment}
                        >
                            Remove auto-comment
                        </Button>
                        <Tooltip title="Clear selection">
                            <IconButton sx={{ml: 'auto'}} onClick={() => setSelectedIds([])}>
                                <CloseIcon/>
                            </IconButton>
                        </Tooltip>
                    </Paper>
                </Portal>
            }
            <BulkReadyCommentDialog
                open={bulkState.isBulkDialogOpen}
                onClose={handleBulkDialogClose}
                productIds={selectedIds}
            />
            <ResponseDialog
                onClose={handleResponseDialogClose}
                open={bulkState.isResponseDialogOpen}
                responseData={bulkState.responseData}
                label={'Auto-comment update'}
            />
        </Box>
    );
}
